import { ArrowLeft, ArrowUpRight, CalendarBlank, EnvelopeSimple, InstagramLogo, MapPin } from "@phosphor-icons/react";
import { SiteFooter } from "../components/SiteFooter";

export function AboutPage() {
  return (
    <>
      <main className="about-page page-enter">
        <nav className="about-page__nav" aria-label="Project navigation">
          <a className="back-link" href="/">
            <ArrowLeft size={18} weight="bold" aria-hidden="true" /> Back to the experience
          </a>
          <span className="eyebrow">About the project</span>
        </nav>

        <header className="about-hero">
          <span className="eyebrow">Madonna / Outernet London / 16 August 2026</span>
          <h1><span>C’mon meet me</span><span>on the dancefloor</span></h1>
          <p>London marks Madonna’s birthday with a free, interactive installation shaped by the fans who come to see it.</p>
        </header>

        <figure className="about-image">
          <picture>
            <source
              media="(max-width: 700px)"
              srcSet="/about-outernet-960.webp"
              width="960"
              height="540"
            />
            <img
              src="/about-outernet-1600.webp"
              alt="The immersive screens inside Outernet London filled with vivid pink visuals"
              width="1600"
              height="900"
              decoding="async"
            />
          </picture>
          <figcaption>Outernet London · Photograph: Alex Wolf and Sebastian Florea</figcaption>
        </figure>

        <section className="about-intro" aria-labelledby="about-release-title">
          <span className="eyebrow">Press release</span>
          <h2 id="about-release-title">London to mark Madonna’s birthday with a new interactive installation</h2>
          <p>The free immersive experience at Outernet London combines large-scale visuals with a fan-led celebration on 16 August.</p>
        </section>

        <div className="about-layout">
          <article className="about-copy">
            <p className="about-copy__lead">A new interactive installation celebrating Madonna’s birthday arrives in central London on Sunday, 16 August. Titled <em>C’MON MEET ME ON THE DANCEFLOOR</em>, the experience brings together music, large-scale visual storytelling and fan participation across the immersive screens of Outernet London.</p>

            <p>Created and directed by London-based Lookport Studio in collaboration with Outernet London, the installation has been designed specifically for the venue’s vast 360-degree digital environment, bringing Madonna’s visual world to an entirely new scale.</p>

            <p>The installation celebrates Madonna’s extraordinary creative legacy while reflecting the visual language of her latest creative chapter, bringing music and imagery together across one of London’s most distinctive digital spaces.</p>

            <p>At the heart of the installation is an interactive fan experience inviting visitors to contribute to the work themselves. Fans can leave a personal message, with selected photographs and messages appearing across Outernet’s monumental screens alongside contributions from other fans.</p>

            <blockquote className="about-quote about-quote--pink">
              <p>“Madonna helped write the language of modern pop culture through decades of relentless work, constant reinvention and a refusal to stand still. We wanted to honour that drive and give the audience a genuine way to express their own connection to her work — through personal stories, emotions and messages.”</p>
              <footer>Alex Wolf <span>Founder and Creative Director, Lookport Studio</span></footer>
            </blockquote>

            <p><em>C’MON MEET ME ON THE DANCEFLOOR</em> is conceived as a public celebration rather than a retrospective — bringing together Madonna’s latest visual language with a more personal layer shaped by the people who come to see it.</p>

            <blockquote className="about-quote about-quote--red">
              <p>“Great cultural moments are about bringing people together and that is exactly what we want Outernet to do. Madonna has inspired audiences and artists for generations, so it is a complete privilege to be able to celebrate her and her connection with fans in such a creative and interactive way.”</p>
              <footer>Philip O’Ferrall <span>CEO, Outernet</span></footer>
            </blockquote>

            <p>The Madonna birthday installation is free and open to the public at Outernet London, Tottenham Court Road, on Sunday, 16 August 2026. The experience screens at selected times throughout the day. Visitors are encouraged to check the Outernet London website for the latest screening schedule before travelling.</p>
          </article>

          <aside className="about-facts" aria-label="Project details">
            <div className="about-fact">
              <CalendarBlank size={26} weight="bold" aria-hidden="true" />
              <div><span>Date</span><strong>Sunday<br />16 August 2026</strong></div>
            </div>
            <div className="about-fact">
              <MapPin size={26} weight="bold" aria-hidden="true" />
              <div><span>Location</span><strong>Outernet London<br />Tottenham Court Road</strong></div>
            </div>
            <div className="about-fact">
              <span className="about-fact__free">Free</span>
              <div><span>Admission</span><strong>Free and open<br />to the public</strong></div>
            </div>

            <section className="about-partner">
              <span className="eyebrow">Created and directed by</span>
              <h3>Lookport Studio</h3>
              <p>Direction and coordination by Alex Wolf.</p>
              <a href="https://www.instagram.com/alexwolf.live/" target="_blank" rel="noreferrer">
                <InstagramLogo size={20} weight="bold" aria-hidden="true" /> @alexwolf.live <ArrowUpRight size={16} weight="bold" aria-hidden="true" />
              </a>
            </section>

            <section className="about-partner">
              <span className="eyebrow">In collaboration with</span>
              <h3>Outernet London</h3>
              <a href="https://www.instagram.com/outernetglobal/" target="_blank" rel="noreferrer">
                <InstagramLogo size={20} weight="bold" aria-hidden="true" /> @outernetglobal <ArrowUpRight size={16} weight="bold" aria-hidden="true" />
              </a>
            </section>

            <section className="about-press">
              <span className="eyebrow">Press enquiries</span>
              <h3>Lookport Studio</h3>
              <a href="mailto:alex@lookport.co.uk"><EnvelopeSimple size={20} weight="bold" aria-hidden="true" /> alex@lookport.co.uk</a>
              <h3>Outernet London</h3>
              <p>nick@outsideorganisation.com<br />andre@outsideorganisation.com</p>
            </section>
          </aside>
        </div>

        <section className="about-credits" aria-labelledby="about-credits-title">
          <header className="about-credits__header">
            <span className="eyebrow">The people behind the project</span>
            <h2 id="about-credits-title">Credits</h2>
          </header>

          <div className="about-credits__grid">
            <section className="about-credit-group">
              <h3>Concept &amp; creative production</h3>
              <ul>
                <li><strong>Alex Wolf</strong><span>Lookport Studio</span></li>
              </ul>
              <a href="https://www.instagram.com/alexwolf.live/" target="_blank" rel="noreferrer">
                <InstagramLogo size={19} weight="bold" aria-hidden="true" /> @alexwolf.live <ArrowUpRight size={15} weight="bold" aria-hidden="true" />
              </a>
            </section>

            <section className="about-credit-group">
              <h3>Warner Music &amp; Parlophone UK</h3>
              <ul>
                <li>Luke Armitage</li>
                <li>Jennifer Ivory</li>
                <li>Jo Heron</li>
                <li>Norman Wonderly</li>
              </ul>
            </section>

            <section className="about-credit-group">
              <h3>Outernet London</h3>
              <ul>
                <li>Jessica Dracup-Holland</li>
                <li>Rowena Maddock</li>
                <li>Sam Webb</li>
                <li>Christina Hayeron</li>
                <li>Luke Snare</li>
                <li>Cory Foley</li>
              </ul>
              <a href="https://www.instagram.com/outernetglobal/" target="_blank" rel="noreferrer">
                <InstagramLogo size={19} weight="bold" aria-hidden="true" /> @outernetglobal <ArrowUpRight size={15} weight="bold" aria-hidden="true" />
              </a>
            </section>

            <section className="about-credit-group about-credit-group--thanks">
              <h3>Special thanks</h3>
              <ul>
                <li><strong>Sebastian Florea</strong><span>(Photos)</span></li>
                <li>Federico Romaniello</li>
                <li>Sophie Hulme</li>
                <li>Jason Schluter</li>
              </ul>
            </section>
          </div>
        </section>
      </main>
      <SiteFooter />
    </>
  );
}
